<template>
  <section id="admin-userGroups-search-summary" class="height-100-p">
    <el-form ref="editForm" :model="editForm" :rules="editFormRules" @submit.native.prevent>
      <el-table
        v-loading="userGroups.loadingSearch"
        :data="userGroups.list"
        size="small"
        tooltip-effect="dark"
        sortable
        class="dark-blue-table"
        :empty-text="$t('common.notAbleToFindRecords')"
        stripe
        @sort-change="handleSortChange"
      >
        <el-table-column
          prop="name"
          :label="$t('admin.userGroups.name')"
          :min-width="4"
          align="left"
          header-align="center"
          sortable="custom"
        >
          <template #default="scope">
            <el-tooltip
              :content="scope.row.name"
              class="item"
              effect="dark"
              placement="top-start"
            >
              <span>{{ scope.row.name }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column
          prop="email"
          :label="$t('admin.userGroups.email')"
          :min-width="4"
          align="center"
          header-align="center"
          sortable="custom"
        >
          <template #default="scope">
            <el-tooltip
              :content="scope.row.email"
              class="item"
              effect="dark"
              placement="top-start"
            >
              <span>{{ scope.row.email }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <!--Group Name (Company name) -->
        <el-table-column
          prop="groups"
          :label="$t('admin.userGroups.companyName')"
          :min-width="4"
          align="center"
          sortable="custom"
        >
          <template #default="scope">
            <el-tooltip
              :content="getCompanyName(scope.row)"
              class="item"
              effect="dark"
              placement="top-start"
            >
              <span>{{ getCompanyName(scope.row) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <!-- Group Field -->
        <el-table-column
          prop="groups"
          :label="$t('admin.userGroups.groups')"
          :min-width="4"
          align="center"
          sortable="custom"
        >
          <template #default="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="groups">
                <el-input
                  ref="value"
                  v-model="editForm.value"
                  v-alphanumeric-validation
                  class="inline-input"
                  size="small"
                  :clearable="scope.row.allowNull"
                />
              </el-form-item>
            </template>

            <template v-else>
              <el-tooltip
                :content="getGroups(scope.row)"
                class="item"
                effect="dark"
                placement="top-start"
              >
                <span>{{ getGroups(scope.row) }}</span>
              </el-tooltip>
            </template>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('common.options')"
          :min-width="1"
          align="center"
        >
          <template #default="scope">
<!--            <assign-role-dialog v-if="scope.row.noRole" :user="scope.row" @success="$emit('refresh-and-clear')"/>-->
            <assign-role-dialog-v2 v-if="scope.row.noRole" :user="scope.row" @success="$emit('refresh-and-clear')"/>
            <select-group-dialog v-else :user="scope.row" :is-shipper="isShipper"/>
          </template>
        </el-table-column>
      </el-table>
      <div v-show="userGroups.total > 0" class="pagination-footer">
        <template>
          <div class="pagination-container">
            <div class="pagination-info">
              <template v-if="userGroups.total >= 1000">
                <el-tooltip
                    effect="dark"
                    content="The search engine limits results to the first 1,000 matches. Please use more specific filters if you cannot find the user."
                    placement="top"
                >
                  <span class="total-label">Total: 1,000+ <i class="el-icon-info"></i></span>
                </el-tooltip>
              </template>
              <template v-else>
                <span class="total-label">Total: {{ userGroups.total }}</span>
              </template>
            </div>

            <el-pagination
                :current-page="userGroups.actualFilters.page"
                :page-sizes="sizes"
                :page-size="userGroups.actualFilters.rows"
                :total="userGroups.total"
                layout="sizes, prev, pager, next, jumper"
                @size-change="handleSearchChangeLimit"
                @current-change="handleSearchChangePage"
            />
          </div>
        </template>
      </div>
    </el-form>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { Message as ElMessage } from 'element-ui';
import constants from '@/utils/constants';
import { USER_GROUPS_SEARCH, USER_GROUPS_SET_EDITING_ROW, USER_GROUPS_UPDATE } from '@/store/actions';
// import AssignRoleDialog from '@/views/admin/userGroups/components/AssignRoleDialog';
import AssignRoleDialogV2 from '@/views/admin/userGroups/components/AssignRoleDialogV2';
import SelectGroupDialog from './components/SelectGroupDialog';


export default {
  name: 'UsersGroupsSummarySection',
  props: {
    isShipper: {
      type: [Boolean, Object],
      default: false,
      required: false
    }
  },
  components: {
    AssignRoleDialogV2,
    // AssignRoleDialog,
    SelectGroupDialog
  },
  computed: {
    ...mapGetters(['userGroups']),
    sizes() {
      return constants.TABLES.DEFAULT_LIMIT_SIZES;
    },
    settingType() {
      return constants.USER_GROUPS.TYPES;
    }
  },
  data() {
    const validateValue = (rule, value, callback) => {
      callback();
    };

    return {
      currentEditingRow: null,
      editForm: {
        id: null,
        name: null,
        value: null
      },
      editFormRules: {
        value: [
          { validator: validateValue, trigger: 'blur' }
        ]
      }
    };
  },
  methods: {
    ...mapActions({
      search: USER_GROUPS_SEARCH,
      setEditingRow: USER_GROUPS_SET_EDITING_ROW,
      update: USER_GROUPS_UPDATE
    }),
    handleSortChange({ prop, order }) {
      const orderFieldSelected = prop === 'groups' ? 'user_metadata.groups.name' : prop;
      this.search({ orderField: orderFieldSelected, orderDirection: constants.TABLES.ORDER_DIRECTION[order] });
    },
    handleSearchChangeLimit(data) {
      this.search({ rows: data, page: constants.TABLES.DEFAULT_PAGE });
    },
    handleSearchChangePage(val) {
      this.search({ page: val });
    },
    handleEdit(row) {
      this.currentEditingRow = row;
      this.setEditingRow(true);
      row.edit = true;
      this.editForm.id = row.id;
      this.editForm.name = row.name;
    },
    handleUpdate(row) {
      this.$refs.editForm.validate(async (valid, validationsErrors) => {
        if (valid) {
          ElMessage.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          this.update(this.editForm).then((response) => {
            this.$message.success(response.message);
            this.search({});
            this.cancelEdit(row);
          }).finally(() => {
            loading.close();
          });
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}. `;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    },
    cancelEdit(row) {
      this.$refs.editForm.resetFields();
      row.edit = false;
      this.setEditingRow(false);
      this.currentEditingRow = null;
    },
    getGroups(row) {
      const groupList = row.user_metadata?.groups;
      if (groupList && groupList.length > 0) {
        if (groupList.length > 2) {
          return `${groupList[0].name}, ${groupList[1].name}...`;
        }
        return groupList.map((group) => group.name).join(', ');
      }
      return '';
    },
    getCompanyName(row) {
      const groupList = row.user_metadata?.groups;
      if (groupList && groupList.length > 0) {
        return `${groupList[0].description}`;
      }
      // return row.email.split('@')[1]?.split('.')[0] || '';
      return null;
    }
  }
};
</script>
<style scoped>
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 20px;
}

.pagination-info {
  margin-right: 15px;
  font-size: 13px;
  color: #606266; /* Standard Element UI text color */
}

.total-label {
  cursor: help;
  font-weight: bold;
}

.el-icon-info {
  margin-left: 5px;
  color: #409eff;
}
</style>
